import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Button,
  Drawer,
  Form,
  InputNumber,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Divider,
  message,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  getInterviewsbyStaffThunk,
  updateInterviewsbyStaffThunk,
  rejectCandidateThunk,
} from "../../../Store/Slices/Admin/jobApplication/jobApplicationThunks";
import ProtectedAction from "../../../Routes/ProtectedRoutes/ProtectedAction";
import { PERMISSIONS } from "../../../config/permission";
import { CiSearch } from "react-icons/ci";
import { FiFilter, FiRefreshCcw } from "react-icons/fi";

const { Option } = Select;

const InterviewMainSection = () => {
  const dispatch = useDispatch();

  const { interviews, loading } = useSelector(
    (state) => state?.admin?.jobApplications
  );

  const [selectedInterview, setSelectedInterview] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchText, setSearchText] = useState("");

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    mode: "",
    date: null,
    page: 1,
    limit: 10,
  });

  const [form] = Form.useForm();

  const updateFilter = (key, value) => {
    setFilters((prev) => {
      const newValue = value === null ? undefined : value;
      if (prev[key] === newValue) return prev;
      return { ...prev, [key]: newValue, page: 1 };
    });
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      updateFilter("search", searchText);
    }, 400);
    return () => clearTimeout(delay);
  }, [searchText]);

  useEffect(() => {
    const cleanedFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== undefined && v !== null && v !== "")
    );
    dispatch(getInterviewsbyStaffThunk(cleanedFilters));
  }, [filters, dispatch]);

  const resetFilters = () => {
    setSelectedDate(null);
    setSearchText("");
    setFilters({ search: "", status: "", mode: "", date: null, page: 1, limit: 10 });
  };

  const openEvaluation = (record) => {
    setSelectedInterview(record);
    form.setFieldsValue({
      score: record?.interview?.score,
      maxScore: record?.interview?.maxScore,
      rating: record?.interview?.rating,
      feedback: record?.interview?.feedback,
      remarks: record?.interview?.remarks,
    });
    setOpen(true);
  };

  const submitEvaluation = async () => {
    try {
      const values = await form.validateFields();
      await dispatch(
        updateInterviewsbyStaffThunk({
          applicationId: selectedInterview?.applicationId,
          interviewId: selectedInterview?.interview?._id,
          data: { ...values, status: "completed" },
        })
      );
      setOpen(false);
      form.resetFields();
      dispatch(getInterviewsbyStaffThunk(filters));
    } catch (error) {
      console.log(error);
    }
  };

  const handleRejectCandidate = async () => {
    try {
      await dispatch(
        rejectCandidateThunk({
          id: selectedInterview?.applicationId,
          performanceSummary: "Rejected by interviewer",
          overallStatus: "rejected",
          isRejected: true,
        })
      ).unwrap();
      message.success("Candidate rejected successfully");
      setOpen(false);
      dispatch(getInterviewsbyStaffThunk(filters));
    } catch (error) {
      message.error(error?.message || "Failed to reject candidate");
    }
  };

  const columns = [
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Candidate
        </span>
      ),
      dataIndex: "applicantName",
      render: (text) => (
        <span className="text-sm font-bold text-gray-800">{text}</span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Email
        </span>
      ),
      dataIndex: "applicantEmail",
      render: (text) => <span className="text-sm text-gray-500">{text}</span>,
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Scheduled Time
        </span>
      ),
      dataIndex: ["interview", "scheduledAt"],
      render: (date) => (
        <span className="text-sm text-gray-700 font-medium">
          {date ? dayjs(date).format("DD MMM YYYY HH:mm") : "—"}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Mode
        </span>
      ),
      dataIndex: ["interview", "mode"],
      render: (mode) => (
        <span
          className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
            mode === "online"
              ? "bg-blue-50 text-blue-600 border-blue-100"
              : "bg-green-50 text-green-600 border-green-100"
          }`}
        >
          {mode}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Status
        </span>
      ),
      dataIndex: ["interview", "status"],
      render: (status) => {
        const map = {
          scheduled: "bg-orange-50 text-orange-500 border-orange-200",
          completed: "bg-green-50 text-green-600 border-green-200",
          cancelled: "bg-red-50 text-red-500 border-red-100",
        };
        const dot = {
          scheduled: "bg-orange-400",
          completed: "bg-green-500",
          cancelled: "bg-red-400",
        };
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${map[status] || "bg-gray-50 text-gray-500 border-gray-100"}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot[status] || "bg-gray-400"}`} />
            {status}
          </span>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Score
        </span>
      ),
      render: (_, record) => (
        <span className="text-sm text-gray-600">
          {record?.interview?.score
            ? `${record.interview.score}/${record.interview.maxScore}`
            : "—"}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Action
        </span>
      ),
      render: (_, record) => {
        if (record?.interview?.status === "completed") {
          return (
            <ProtectedAction requiredPermission={PERMISSIONS.EDIT_JOB_INTERVIEW}>
              <button
                onClick={() => openEvaluation(record)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 transition"
              >
                Edit
              </button>
            </ProtectedAction>
          );
        }
        return (
          <ProtectedAction requiredPermission={PERMISSIONS.EVALUATE_JOB_INTERVIEW}>
            <button
              className="btn-gradient text-xs px-3 py-1.5 rounded-lg"
              onClick={() => openEvaluation(record)}
            >
              Evaluate
            </button>
          </ProtectedAction>
        );
      },
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 flex flex-col gap-5">

      {/* ── Page Header ───────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h2 className="page-heading w-48">My Interviews</h2>
        <p className="subheading">
          Review and evaluate your scheduled interviews.
        </p>
      </div>

      {/* ── Filter bar ────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 flex items-center gap-3 flex-wrap">

        {/* Search */}
        <div className="relative flex-1 min-w-[160px]">
          <CiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search candidate"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-transparent border-0 focus:outline-none text-gray-700 placeholder-gray-400"
          />
        </div>

        <div className="w-px h-6 bg-gray-200 flex-shrink-0" />

        {/* Status */}
        <div className="flex items-center gap-1.5">
          <FiFilter className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <Select
            placeholder="Status"
            allowClear
            bordered={false}
            style={{ minWidth: 110 }}
            size="small"
            value={filters.status || undefined}
            onChange={(value) => updateFilter("status", value)}
          >
            <Option value="scheduled">Scheduled</Option>
            <Option value="completed">Completed</Option>
          </Select>
        </div>

        <div className="w-px h-6 bg-gray-200 flex-shrink-0" />

        {/* Mode */}
        <Select
          placeholder="Mode"
          allowClear
          bordered={false}
          style={{ minWidth: 100 }}
          size="small"
          value={filters.mode || undefined}
          onChange={(value) => updateFilter("mode", value)}
        >
          <Option value="online">Online</Option>
          <Option value="offline">Offline</Option>
        </Select>

        <div className="w-px h-6 bg-gray-200 flex-shrink-0" />

        {/* Date */}
        <DatePicker
          bordered={false}
          size="small"
          value={selectedDate}
          onChange={(date) => {
            setSelectedDate(date);
            updateFilter("date", date ? date.format("YYYY-MM-DD") : null);
          }}
          style={{ minWidth: 130 }}
        />

        <div className="w-px h-6 bg-gray-200 flex-shrink-0" />

        {/* Reset */}
        <button
          onClick={resetFilters}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-purple-600 font-medium transition whitespace-nowrap px-1"
        >
          <FiRefreshCcw className="w-3.5 h-3.5" />
          Reset
        </button>
      </div>

      {/* ── Table ─────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <Table
          rowKey={(row) => row?.interview?._id}
          columns={columns}
          dataSource={Array.isArray(interviews) ? interviews : []}
          loading={loading}
          rowClassName="hover:bg-gray-50 transition-colors"
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            size: "small",
            onChange: (page, pageSize) =>
              setFilters((prev) => ({ ...prev, page, limit: pageSize })),
          }}
        />
      </div>

      {/* ── Evaluate Drawer ───────────────────────────── */}
      <Drawer
        title={
          <span className="text-base font-bold text-gray-800">
            Evaluate Interview
          </span>
        }
        open={open}
        width={420}
        onClose={() => setOpen(false)}
        bodyStyle={{ background: "#f9fafb", padding: "20px" }}
        extra={
          <button className="btn-gradient px-4 py-2 text-sm" onClick={submitEvaluation}>
            Save Evaluation
          </button>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
              Evaluation Details
            </p>
            <Form form={form} layout="vertical">
              <Form.Item
                label={<span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Score</span>}
                name="score"
                rules={[
                  { required: true },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const maxScore = getFieldValue("maxScore");
                      if (!value || !maxScore) return Promise.resolve();
                      if (value > maxScore) {
                        return Promise.reject(new Error("Score cannot be greater than Max Score"));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber style={{ width: "100%" }} className="rounded-xl" />
              </Form.Item>

              <Form.Item
                label={<span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Max Score</span>}
                name="maxScore"
                rules={[{ required: true }]}
              >
                <InputNumber style={{ width: "100%" }} className="rounded-xl" />
              </Form.Item>

              <Form.Item
                label={<span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Rating</span>}
                name="rating"
                rules={[{ required: true }]}
              >
                <Select className="rounded-xl">
                  {[1, 2, 3, 4, 5].map((v) => (
                    <Option key={v} value={v}>{v}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={<span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Feedback</span>}
                name="feedback"
              >
                <Input.TextArea rows={3} className="rounded-xl" />
              </Form.Item>

              <Form.Item
                label={<span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Remarks</span>}
                name="remarks"
              >
                <Input.TextArea rows={3} className="rounded-xl" />
              </Form.Item>
            </Form>
          </div>

          <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-4">
            <p className="text-xs font-bold uppercase tracking-widest text-red-400 mb-3">
              Danger Zone
            </p>
            <ProtectedAction requiredPermission={PERMISSIONS.REJECT_CANDIDATE}>
              <button
                onClick={handleRejectCandidate}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-red-500 border border-red-200 bg-red-50 hover:bg-red-100 transition"
              >
                Reject Candidate
              </button>
            </ProtectedAction>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default InterviewMainSection;